import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Divider, 
  Dialog,
  Collapse,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  AddCircleOutline as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Home as HomeIcon,
  LocalGroceryStore as FoodIcon,
  DriveEta as TransportIcon,
  LocalHospital as HealthIcon,
  SportsEsports as EntertainmentIcon,
  School as EducationIcon,
  ShoppingBag as ShoppingIcon,
  Category as DefaultIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  MonetizationOn as MoneyIcon,
  Savings as SavingsIcon,
  CreditCard as CreditIcon,
  Restaurant as RestaurantIcon,
  Build as UtilitiesIcon,
  LocalGasStation as GasIcon,
  Phone as PhoneIcon,
  FitnessCenter as FitnessIcon,
  Movie as MovieIcon,
  FlightTakeoff as TravelIcon,
  Pets as PetsIcon,
  ChildCare as ChildCareIcon,
  AttachMoney as TaxIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import budgetService from '../../budgeting/services/budgetService';
import BudgetStatsDashboard from '../../budgeting/components/BudgetStatsDashboard';
import BudgetForm from '../../budgeting/components/BudgetForm';
import GuidedBudgetModal from '../../budgeting/components/GuidedBudgetModal';
import toast from 'react-hot-toast';

// Simplified category icon mapping to prevent React error #31
const CATEGORY_ICONS = {
  'Housing': { icon: 'HomeIcon', color: 'from-blue-500 to-blue-600', bgColor: 'bg-blue-50', textColor: 'text-blue-700' },
  'Transportation': { icon: 'TransportIcon', color: 'from-green-500 to-green-600', bgColor: 'bg-green-50', textColor: 'text-green-700' },
  'Food': { icon: 'FoodIcon', color: 'from-orange-500 to-orange-600', bgColor: 'bg-orange-50', textColor: 'text-orange-700' },
  'Utilities': { icon: 'UtilitiesIcon', color: 'from-yellow-500 to-yellow-600', bgColor: 'bg-yellow-50', textColor: 'text-yellow-700' },
  'Healthcare': { icon: 'HealthIcon', color: 'from-red-500 to-red-600', bgColor: 'bg-red-50', textColor: 'text-red-700' },
  'Entertainment': { icon: 'EntertainmentIcon', color: 'from-purple-500 to-purple-600', bgColor: 'bg-purple-50', textColor: 'text-purple-700' },
  'Education': { icon: 'EducationIcon', color: 'from-indigo-500 to-indigo-600', bgColor: 'bg-indigo-50', textColor: 'text-indigo-700' },
  'Shopping': { icon: 'ShoppingIcon', color: 'from-pink-500 to-pink-600', bgColor: 'bg-pink-50', textColor: 'text-pink-700' },
  'Restaurants': { icon: 'RestaurantIcon', color: 'from-amber-500 to-amber-600', bgColor: 'bg-amber-50', textColor: 'text-amber-700' },
  'Gas': { icon: 'GasIcon', color: 'from-gray-500 to-gray-600', bgColor: 'bg-gray-50', textColor: 'text-gray-700' },
  'Phone': { icon: 'PhoneIcon', color: 'from-cyan-500 to-cyan-600', bgColor: 'bg-cyan-50', textColor: 'text-cyan-700' },
  'Fitness': { icon: 'FitnessIcon', color: 'from-emerald-500 to-emerald-600', bgColor: 'bg-emerald-50', textColor: 'text-emerald-700' },
  'Movies': { icon: 'MovieIcon', color: 'from-violet-500 to-violet-600', bgColor: 'bg-violet-50', textColor: 'text-violet-700' },
  'Travel': { icon: 'TravelIcon', color: 'from-sky-500 to-sky-600', bgColor: 'bg-sky-50', textColor: 'text-sky-700' },
  'Pets': { icon: 'PetsIcon', color: 'from-lime-500 to-lime-600', bgColor: 'bg-lime-50', textColor: 'text-lime-700' },
  'Child Care': { icon: 'ChildCareIcon', color: 'from-rose-500 to-rose-600', bgColor: 'bg-rose-50', textColor: 'text-rose-700' },
  'Taxes': { icon: 'TaxIcon', color: 'from-slate-500 to-slate-600', bgColor: 'bg-slate-50', textColor: 'text-slate-700' },
  'Savings': { icon: 'SavingsIcon', color: 'from-teal-500 to-teal-600', bgColor: 'bg-teal-50', textColor: 'text-teal-700' },
  'Debt': { icon: 'CreditIcon', color: 'from-red-600 to-red-700', bgColor: 'bg-red-50', textColor: 'text-red-700' }
};

const DEFAULT_CATEGORY = { icon: 'DefaultIcon', color: 'from-gray-500 to-gray-600', bgColor: 'bg-gray-50', textColor: 'text-gray-700' };

// Icon component mapping to prevent render errors
const ICON_COMPONENTS = {
  'HomeIcon': HomeIcon,
  'TransportIcon': TransportIcon,
  'FoodIcon': FoodIcon,
  'UtilitiesIcon': UtilitiesIcon,
  'HealthIcon': HealthIcon,
  'EntertainmentIcon': EntertainmentIcon,
  'EducationIcon': EducationIcon,
  'ShoppingIcon': ShoppingIcon,
  'RestaurantIcon': RestaurantIcon,
  'GasIcon': GasIcon,
  'PhoneIcon': PhoneIcon,
  'FitnessIcon': FitnessIcon,
  'MovieIcon': MovieIcon,
  'TravelIcon': TravelIcon,
  'PetsIcon': PetsIcon,
  'ChildCareIcon': ChildCareIcon,
  'TaxIcon': TaxIcon,
  'SavingsIcon': SavingsIcon,
  'CreditIcon': CreditIcon,
  'DefaultIcon': DefaultIcon
};

// Helper function to safely render category icons
const renderCategoryIcon = (categoryName, className = "text-sm sm:text-base") => {
  const categoryConfig = CATEGORY_ICONS[categoryName] || DEFAULT_CATEGORY;
  const iconName = categoryConfig.icon;
  const IconComponent = ICON_COMPONENTS[iconName] || ICON_COMPONENTS['DefaultIcon'];
  
  return <IconComponent className={className} />;
};

/**
 * Enhanced Budget Card Component with expandable details and inline editing
 */
const EnhancedBudgetCard = ({ budget, onEdit, onDelete, isOnlyBudget = false, onBudgetUpdate }) => {
  const [expanded, setExpanded] = useState(isOnlyBudget);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [editedAmounts, setEditedAmounts] = useState({}); // category index -> amount
  const [sliderBaselines, setSliderBaselines] = useState({}); // category index -> initial amount
  const [savingCategory, setSavingCategory] = useState(null);
  
  const totalPlanned = budget.totalPlannedExpenses || 0;
  const totalActual = budget.totalActualExpenses || 0;
  const totalIncome = budget.totalPlannedIncome || 0;
  const balance = totalIncome - totalPlanned;
  
  // Budget Progress: How much of available funds (income) are allocated to budget
  const budgetToIncomePercentage = totalIncome > 0 ? (totalPlanned / totalIncome) * 100 : 0;

  // US States for dropdown
  const US_STATES = [
    { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
  ];

  const initializeEditForm = () => {
    setEditFormData({
      name: budget.name || '',
      description: budget.description || '',
      periodType: budget.period?.type || 'monthly',
      budgetMethod: budget.budgetMethod || 'zero-based',
      startDate: budget.period?.startDate ? budget.period.startDate.split('T')[0] : '',
      endDate: budget.period?.endDate ? budget.period.endDate.split('T')[0] : '',
      state: budget.location?.state || '',
      city: budget.location?.city || '',
      autoRollover: budget.preferences?.autoRollover || false,
      notifications: budget.preferences?.notifications || true
    });
  };

  useEffect(() => {
    initializeEditForm();
  }, [budget]);

  const handleEditFieldChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setShowActionMenu(false);
    initializeEditForm();
  };

  const handleEditCancel = () => {
    setIsEditMode(false);
    initializeEditForm();
  };

  const handleEditSave = async () => {
    try {
      setIsSaving(true);
      // Here you would typically call an API to update the budget
      // await budgetService.updateBudget(budget._id, editFormData);
      console.log('Saving budget:', editFormData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsEditMode(false);
    } catch (error) {
      console.error('Error saving budget:', error);
      // Handle error (show toast, etc.)
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Category ranges for percentage calculations (similar to ExpenseSection)
  const CATEGORY_RANGES = {
    Housing: [20, 35], 
    Utilities: [3, 8], 
    Food: [10, 20], 
    Transportation: [10, 15], 
    Insurance: [3, 10], 
    Healthcare: [2, 8], 
    Entertainment: [1, 4], 
    Shopping: [5, 15],
    Dining: [5, 15],
    Restaurants: [5, 15],
    Other: [0, 3],
    Savings: [10, 20],
    Debt: [5, 15],
    Education: [2, 10],
    Travel: [1, 5],
    Pets: [1, 3]
  };

  const getBandFor = (category, pct) => {
    const [low, high] = CATEGORY_RANGES[category] || [1, 10];
    if (pct < low) return { label: 'Low', color: 'text-teal-600' };
    if (pct > high) return { label: 'High', color: 'text-rose-600' };
    return { label: 'Just Right', color: 'text-emerald-600' };
  };

  // Initialize slider baselines when categories change or view mode changes
  useEffect(() => {
    if (expanded && budget.categories) {
      const baselines = {};
      budget.categories.forEach((cat, index) => {
        baselines[index] = cat.plannedAmount || 0;
      });
      setSliderBaselines(baselines);
    }
  }, [expanded, budget.categories]);

  // Handle category amount update
  const handleCategoryUpdate = async (categoryIndex, newAmount) => {
    try {
      setSavingCategory(categoryIndex);
      const category = budget.categories[categoryIndex];
      if (!category) return;

      // Update the category in the budget
      const updatedCategories = [...budget.categories];
      updatedCategories[categoryIndex] = {
        ...category,
        plannedAmount: newAmount
      };

      // Recalculate total planned expenses
      const newTotalPlanned = updatedCategories.reduce((sum, cat) => sum + (cat.plannedAmount || 0), 0);

      // Update budget via API
      const updatedBudget = {
        ...budget,
        categories: updatedCategories,
        totalPlannedExpenses: newTotalPlanned
      };

      await budgetService.updateBudget(budget._id, updatedBudget);
      
      // Update local state
      setSliderBaselines(prev => ({ ...prev, [categoryIndex]: newAmount }));
      setEditedAmounts(prev => {
        const updated = { ...prev };
        delete updated[categoryIndex];
        return updated;
      });

      // Notify parent to refresh budgets
      if (onBudgetUpdate) {
        onBudgetUpdate();
      }

      toast.success('Category updated successfully');
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    } finally {
      setSavingCategory(null);
    }
  };

  return (
    <Card className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-0 bg-gradient-to-br from-white via-white to-gray-50/50 backdrop-blur-sm">
      <CardContent className="p-0">
        {/* Header Section - Mobile Optimized */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 p-3 sm:p-4 lg:p-6 text-white">
          {/* Mobile: Stack layout, Desktop: Side by side */}
          <div className="space-y-3 sm:space-y-0 sm:flex sm:justify-between sm:items-start">
            <div className="flex-1 min-w-0 sm:mr-4">
              {isEditMode ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editFormData.name || ''}
                    onChange={(e) => handleEditFieldChange('name', e.target.value)}
                    className="w-full px-2 py-1 text-sm sm:text-base font-bold bg-white/20 text-white placeholder-white/70 border border-white/30 rounded"
                    placeholder="Budget name"
                  />
                  <input
                    type="text"
                    value={editFormData.description || ''}
                    onChange={(e) => handleEditFieldChange('description', e.target.value)}
                    className="w-full px-2 py-1 text-xs sm:text-sm bg-white/20 text-white placeholder-white/70 border border-white/30 rounded"
                    placeholder="Budget description"
                  />
                </div>
              ) : (
                <>
                  <Typography variant="h5" className="text-base sm:text-lg lg:text-xl font-bold text-white mb-1 sm:mb-2">
                    {budget.name}
                  </Typography>
                </>
              )}
              
              <div className="flex flex-wrap gap-1 sm:gap-2">
                {budget.isPrimary && (
                  <Chip 
                    label="Primary" 
                    size="small" 
                    className="bg-white/20 text-white border-white/30 text-xs h-5 sm:h-6" 
                  />
                )}
              </div>
            </div>
            
            {/* Mobile: Income display first, then actions */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
              {/* Total Income - Always visible */}
              <div className="text-center sm:text-right mb-2 sm:mb-0">
                <div className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                  {formatCurrency(totalIncome)}
                </div>
                <div className="text-xs text-indigo-200">
                  Total Income
                </div>
              </div>
              
              {/* Action Menu */}
              <div className="relative flex justify-center sm:justify-end">
                {isEditMode ? (
                  <div className="flex gap-2">
                    <button
                      onClick={handleEditSave}
                      disabled={isSaving}
                      className="px-3 py-1.5 bg-green-600 text-white border border-green-500 rounded hover:bg-green-700 transition-colors text-xs disabled:opacity-50"
                      title="Save changes"
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleEditCancel}
                      className="px-3 py-1.5 bg-gray-600 text-white border border-gray-500 rounded hover:bg-gray-700 transition-colors text-xs"
                      title="Cancel editing"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete "${budget.name}"? This action cannot be undone.`)) {
                          onDelete?.(budget._id);
                        }
                      }}
                      className="px-3 py-1.5 bg-red-600 text-white border border-red-500 rounded hover:bg-red-700 transition-colors text-xs flex items-center gap-1"
                      title="Delete budget"
                    >
                      <DeleteIcon className="text-sm" style={{ fontSize: '14px' }} />
                      Delete
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Mobile: Show menu button */}
                    <div className="sm:hidden">
                      <button
                        onClick={() => setShowActionMenu(!showActionMenu)}
                        className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/20"
                        title="Actions"
                      >
                        <span className="text-sm">⋯</span>
                      </button>
                      
                      {/* Action Menu Dropdown */}
                      {showActionMenu && (
                        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[100px]">
                          <button
                            onClick={handleEditClick}
                            className="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                          >
                            Edit Budget
                          </button>
                          <button
                            onClick={() => onEdit(budget._id)}
                            className="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                          >
                            Full Edit
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Desktop: Show buttons */}
                    <div className="hidden sm:flex gap-2">
                      <button
                        onClick={handleEditClick}
                        className="px-3 py-1.5 bg-indigo-600 text-white border border-indigo-500 rounded hover:bg-indigo-700 transition-colors text-xs"
                        title="Edit budget"
                      >
                        Edit
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Section - Mobile Optimized */}
        <div className="p-3 sm:p-4 lg:p-6">
          {/* Mobile: Single column for very small screens, 2 columns for small, 3 for desktop */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-6">
            <div className="text-center p-2 sm:p-3 lg:p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg sm:rounded-xl">
              <MoneyIcon className="text-blue-600 mx-auto mb-1 sm:mb-2 text-sm sm:text-base lg:text-lg" />
              <div className="text-sm sm:text-lg lg:text-xl font-bold text-blue-700">
                {formatCurrency(totalPlanned)}
              </div>
              <div className="text-xs text-blue-600 leading-tight">Planned</div>
            </div>
            
            <div className="text-center p-2 sm:p-3 lg:p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg sm:rounded-xl">
              <TrendingUpIcon className="text-orange-600 mx-auto mb-1 sm:mb-2 text-sm sm:text-base lg:text-lg" />
              <div className="text-sm sm:text-lg lg:text-xl font-bold text-orange-700">
                {formatCurrency(totalActual)}
              </div>
              <div className="text-xs text-orange-600 leading-tight">Spent</div>
            </div>
            
            <div className="col-span-2 sm:col-span-1 text-center p-2 sm:p-3 lg:p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg sm:rounded-xl">
              <SavingsIcon className={`mx-auto mb-1 sm:mb-2 text-sm sm:text-base lg:text-lg ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`} />
              <div className={`text-sm sm:text-lg lg:text-xl font-bold ${balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {formatCurrency(balance)}
              </div>
              <div className={`text-xs leading-tight ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {balance >= 0 ? 'Surplus' : 'Deficit'}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-4 sm:mb-6">
            <div className="flex justify-between mb-2">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Budget Progress</span>
              <span className="text-xs sm:text-sm text-gray-600">{budgetToIncomePercentage.toFixed(1)}% used</span>
            </div>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(budgetToIncomePercentage, 100)}
              className="h-2 sm:h-3 rounded-full"
              sx={{
                backgroundColor: '#e5e7eb',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: budgetToIncomePercentage > 90 ? '#ef4444' : budgetToIncomePercentage > 75 ? '#f59e0b' : '#10b981',
                  borderRadius: '9999px'
                }
              }}
            />
          </div>

          {/* Expand Button and Actions - Mobile Optimized */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
            <Button
              onClick={() => setExpanded(!expanded)}
              startIcon={expanded ? <ExpandLessIcon sx={{ fontSize: { xs: 14, sm: 16 } }} /> : <ExpandMoreIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              className="text-indigo-600 hover:bg-indigo-50 text-xs sm:text-sm p-1 sm:p-2"
              size="small"
            >
              <span className="hidden sm:inline">{expanded ? 'Hide Details' : 'View Categories'}</span>
              <span className="sm:hidden">{expanded ? '▲ Hide' : '▼ Details'}</span>
            </Button>
            
            {!isEditMode && (
              <div className="flex gap-1 sm:gap-2">
                {/* Mobile: Icon buttons, Desktop: Text buttons */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onEdit(budget._id)}
                  className="sm:hidden border-indigo-600 text-indigo-600 hover:bg-indigo-50 min-w-0 p-1"
                  title="Full Edit"
                >
                  <span className="text-sm">✏️</span>
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => onEdit(budget._id)}
                  className="hidden sm:flex bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm px-2 sm:px-4"
                >
                  Full Edit
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Expandable Details Section */}
        <Collapse in={expanded}>
          <div className="px-4 sm:px-6 pb-4 sm:pb-6">
            <Divider className="mb-4" />
            
            {isEditMode ? (
              <div className="space-y-4">
                {/* Budget Settings Section */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Budget Settings</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Period Type</label>
                      <select
                        value={editFormData.periodType || ''}
                        onChange={(e) => handleEditFieldChange('periodType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Budget Method</label>
                      <select
                        value={editFormData.budgetMethod || ''}
                        onChange={(e) => handleEditFieldChange('budgetMethod', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="zero-based">Zero-Based</option>
                        <option value="50-30-20">50/30/20 Rule</option>
                        <option value="envelope">Envelope Method</option>
                        <option value="percentage">Percentage-Based</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={editFormData.startDate || ''}
                        onChange={(e) => handleEditFieldChange('startDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                      <input
                        type="date"
                        value={editFormData.endDate || ''}
                        onChange={(e) => handleEditFieldChange('endDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Location & Preferences */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Location & Preferences</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <select
                        value={editFormData.state || ''}
                        onChange={(e) => handleEditFieldChange('state', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select State</option>
                        {US_STATES.map(state => (
                          <option key={state.value} value={state.value}>{state.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        value={editFormData.city || ''}
                        onChange={(e) => handleEditFieldChange('city', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Enter city"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="autoRollover"
                        checked={editFormData.autoRollover || false}
                        onChange={(e) => handleEditFieldChange('autoRollover', e.target.checked)}
                        className="mr-2"
                      />
                      <label htmlFor="autoRollover" className="text-sm text-gray-700">Auto-rollover unused budget</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="notifications"
                        checked={editFormData.notifications !== false}
                        onChange={(e) => handleEditFieldChange('notifications', e.target.checked)}
                        className="mr-2"
                      />
                      <label htmlFor="notifications" className="text-sm text-gray-700">Enable notifications</label>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <Typography variant="h6" className="text-gray-800 font-semibold">
                    Category Breakdown
                  </Typography>
                </div>
                
                {budget.categories && budget.categories.length > 0 ? (
                  <div className="space-y-5">
                    <div className="text-base text-gray-700 -mt-2">Adjust amounts. Percentages are of your total income.</div>
                    {(() => {
                      // Group categories by name (similar to ExpenseSection)
                      // This allows multiple budget categories with the same name to be grouped together
                      const grouped = {};
                      budget.categories.forEach((category, index) => {
                        const name = category.name || 'Other';
                        if (!grouped[name]) {
                          grouped[name] = [];
                        }
                        grouped[name].push({ ...category, originalIndex: index });
                      });

                      return Object.entries(grouped).map(([categoryName, categories]) => {
                        const subtotal = categories.reduce((sum, cat) => sum + (cat.plannedAmount || 0), 0);
                        const pctCat = totalIncome > 0 ? Math.round((subtotal / totalIncome) * 100) : 0;
                        const [low, high] = CATEGORY_RANGES[categoryName] || [1, 10];
                        const categoryConfig = CATEGORY_ICONS[categoryName] || DEFAULT_CATEGORY;

                        return (
                          <div key={categoryName} className="rounded-lg border border-gray-200 bg-gray-50">
                            {/* Category Header */}
                            <div
                              className="flex items-center justify-between px-3 py-3 rounded-t-lg"
                              style={{ backgroundColor: categoryConfig.bgColor || '#F3F4F6' }}
                            >
                              <div className="flex items-center min-w-0">
                                <div className={`p-1.5 rounded-lg ${categoryConfig.bgColor} flex-shrink-0 mr-2`}>
                                  {renderCategoryIcon(categoryName, "text-sm")}
                                </div>
                                <h3 className="text-lg font-semibold truncate" style={{ color: categoryConfig.textColor || '#111827' }}>
                                  {categoryName}
                                </h3>
                              </div>
                              <div className="text-sm" style={{ color: categoryConfig.textColor || '#374151' }}>
                                {formatCurrency(subtotal)} · {pctCat}%
                              </div>
                            </div>

                            {/* Category Items with Sliders */}
                            <div className="space-y-3 p-3">
                              {categories.map((category) => {
                                const index = category.originalIndex;
                                const baseline = sliderBaselines[index] ?? category.plannedAmount ?? 0;
                                const current = editedAmounts[index] ?? baseline;
                                const highAbs = Math.round((high * totalIncome) / 100);
                                const baseAbs = Math.round((baseline || 0) * 1.5);
                                const sliderMin = 0;
                                const hardCap = Math.max(0, Math.round((totalIncome || 0) * 0.5));
                                const sliderMax = Math.min(Math.max(highAbs, baseAbs), hardCap || Math.max(highAbs, baseAbs));
                                const value = Math.max(sliderMin, Math.min(current, sliderMax));
                                const pct = totalIncome > 0 ? (value / totalIncome) * 100 : 0;
                                const band = getBandFor(categoryName, pct);
                                const isHigh = pct > (CATEGORY_RANGES[categoryName]?.[1] ?? 10);
                                const hasChanges = editedAmounts[index] !== undefined && editedAmounts[index] !== baseline;

                                return (
                                  <div key={index} className={`rounded-md p-4 ${isHigh ? 'bg-rose-50' : 'bg-white'}`}>
                                    <div className="flex items-center justify-between">
                                      <div className="text-base font-medium text-gray-900 truncate">
                                        {category.name || categoryName}
                                        {category.notes && (
                                          <span className="ml-2 text-sm text-gray-500">({category.notes})</span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <div className="text-sm text-gray-500">
                                          {pct.toFixed(1)}% <span className={band.color}>· {band.label}</span>
                                        </div>
                                        {hasChanges && (
                                          <button
                                            onClick={() => {
                                              setEditedAmounts(prev => {
                                                const updated = { ...prev };
                                                delete updated[index];
                                                return updated;
                                              });
                                            }}
                                            className="text-sm px-2 py-1 border border-gray-200 text-gray-700 rounded hover:bg-gray-50"
                                            title="Revert changes"
                                          >
                                            Revert
                                          </button>
                                        )}
                                        {savingCategory === index ? (
                                          <span className="text-xs text-gray-500">Saving...</span>
                                        ) : hasChanges ? (
                                          <button
                                            onClick={() => handleCategoryUpdate(index, value)}
                                            className="text-sm px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                            title="Save changes"
                                          >
                                            Save
                                          </button>
                                        ) : null}
                                      </div>
                                    </div>
                                    <div className="mt-2">
                                      <input
                                        type="range"
                                        min={sliderMin}
                                        max={sliderMax}
                                        value={value}
                                        onChange={(e) => {
                                          const v = Math.max(sliderMin, Math.min(parseFloat(e.target.value || '0'), sliderMax));
                                          setEditedAmounts(prev => ({ ...prev, [index]: v }));
                                        }}
                                        className="w-full tw-slider"
                                        style={{
                                          background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${(value / sliderMax) * 100}%, #e5e7eb ${(value / sliderMax) * 100}%, #e5e7eb 100%)`
                                        }}
                                      />
                                    </div>
                                    <div className="mt-3 flex items-center gap-3">
                                      <div className="text-base text-gray-900 font-medium">{formatCurrency(value)}</div>
                                      <div className="text-sm text-gray-500">/mo · Low {low}% · High {high}%</div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  <Typography variant="body2" color="text.secondary" className="text-center py-8">
                    No categories defined yet. Edit budget to add categories.
                  </Typography>
                )}
              </>
            )}
          </div>
        </Collapse>
      </CardContent>
    </Card>
  );
};

/**
 * Budget Section Component
 * 
 * Displays all budgets for the user with enhanced UI and expandable details
 */
const BudgetSection = ({ income, expenses }) => {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [guidedBudgetOpen, setGuidedBudgetOpen] = useState(false);

  const safeExpenses = Array.isArray(expenses) ? expenses : [];
  const safeBudgets = Array.isArray(budgets) ? budgets : [];

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const budgetsData = await budgetService.getAllBudgets();
      setBudgets(budgetsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching budgets:', err);
      setError('Failed to load budgets');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBudgets();
  }, []);

  const handleCreateBudget = () => {
    setEditingBudget(null);
    setOpen(true);
  };

  const handleGuidedBudget = () => {
    setGuidedBudgetOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setEditOpen(false);
    setEditingBudget(null);
  };

  const handleBudgetCreated = () => {
    setOpen(false);
    setEditOpen(false);
    setEditingBudget(null);
    fetchBudgets();
  };

  const handleEditBudget = (budgetId) => {
    const budgetToEdit = safeBudgets.find(b => b._id === budgetId);
    setEditingBudget(budgetToEdit);
    setEditOpen(true);
  };

  const handleDeleteBudget = async (budgetId) => {
    try {
      await budgetService.deleteBudget(budgetId);
      toast.success('Budget deleted successfully');
      fetchBudgets();
    } catch (error) {
      console.error('Error deleting budget:', error);
      toast.error('Failed to delete budget');
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white via-gray-50 to-indigo-50 rounded-xl shadow-lg p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-64 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-xl p-6 text-red-800 shadow-lg">
        <Typography variant="h6" className="font-bold mb-2">Error Loading Budgets</Typography>
        <Typography variant="body2">{error}</Typography>
        <Button 
          variant="contained" 
          onClick={fetchBudgets}
          className="mt-4 bg-red-600 hover:bg-red-700"
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white via-gray-50 to-indigo-50 rounded-xl shadow-lg p-4 sm:p-6">
      {/* Enhanced Header with Stats Dashboard */}
      <div className="mb-6 sm:mb-8">
        <BudgetStatsDashboard />
      </div>
      
      {/* Mobile-Optimized Header Section */}
      <div className="mb-6 sm:mb-8">
        {/* Title Section */}
        <div className="mb-4 sm:mb-6">
          <Typography variant="h4" className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Your Budgets
          </Typography>
          <Typography variant="body1" className="text-xs sm:text-sm text-gray-600 mt-1">
            Manage and track your financial goals with detailed category breakdowns
          </Typography>
        </div>
        
        {/* Create Budget Buttons - Full Width on Mobile */}
        <div className="w-full flex flex-col sm:flex-row gap-2 sm:gap-3">
          <Button
            variant="contained"
            size="medium"
            startIcon={<AddIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
            onClick={handleCreateBudget}
            className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base"
          >
            <span className="sm:hidden">Create New Budget</span>
            <span className="hidden sm:inline">Create Budget</span>
          </Button>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<TrendingUpIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
            onClick={handleGuidedBudget}
            className="w-full sm:w-auto border-indigo-600 text-indigo-600 hover:bg-indigo-50 shadow-md hover:shadow-lg transition-all duration-300 px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base"
          >
            <span className="sm:hidden">Guided Budget</span>
            <span className="hidden sm:inline">Guided Budget</span>
          </Button>
        </div>
      </div>

      <Dialog open={open} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <BudgetForm onSuccess={handleBudgetCreated} onCancel={handleCloseModal} />
      </Dialog>

      {/* Edit Budget Dialog */}
      <Dialog open={editOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <BudgetForm 
          budget={editingBudget} 
          onSuccess={handleBudgetCreated} 
          onCancel={handleCloseModal} 
        />
      </Dialog>

      {/* Guided Budget Modal */}
      <GuidedBudgetModal
        open={guidedBudgetOpen}
        onClose={() => setGuidedBudgetOpen(false)}
        onSuccess={handleBudgetCreated}
      />

      {safeBudgets.length === 0 ? (
        <Card className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 border-2 border-dashed border-indigo-200">
          <CardContent className="text-center p-8 sm:p-12">
            <div className="bg-gradient-to-br from-indigo-100 to-purple-100 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <MoneyIcon className="text-2xl sm:text-4xl text-indigo-600" />
            </div>
            <Typography variant="h5" className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">
              No budgets found
            </Typography>
            <Typography variant="body1" className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 max-w-md mx-auto">
              Create your first budget to start tracking your spending against your financial goals with detailed category insights.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleCreateBudget}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Create Your First Budget
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          {safeBudgets.map((budget) => (
            <EnhancedBudgetCard 
              key={budget._id} 
              budget={budget} 
              onEdit={handleEditBudget}
              onDelete={handleDeleteBudget}
              isOnlyBudget={safeBudgets.length === 1}
              onBudgetUpdate={fetchBudgets}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BudgetSection; 